import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ShoppingCart, MagnifyingGlass, ArrowUpRight, Storefront, Tag,
  Spinner, ShieldCheck, Package, Sparkle, Broom, Bed, Wrench, Hammer, DeviceMobile,
  Truck, MapPin, Barcode, CheckCircle,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { trackProductView } from "@/lib/recentlyViewed";
import ProductLightbox from "@/components/ProductLightbox";

const CATEGORY_ICON = {
  appliances: Package,
  "appliance-parts": Wrench,
  cleaning: Broom,
  furniture: Bed,
  tools: Hammer,
  electronics: DeviceMobile,
};

const emptyOrder = {
  name: "", email: "", phone: "", address: "",
  delivery_option: "ship", install_later: true, notes: "",
};

const PartsOrderModal = ({ open, onOpenChange, product, promo }) => {
  const [form, setForm] = useState(emptyOrder);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => { if (open) { setForm(emptyOrder); setConfirmed(null); } }, [open]);
  if (!product) return null;

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const listPrice = Number(product.list_price ?? product.price ?? 0);
  const pickupFee = Number(promo?.shop_local_pickup_fee ?? 15);
  const localCount = (product.local_sources || []).length;

  const optionTotal = form.delivery_option === "pickup" ? listPrice + pickupFee : (form.delivery_option === "self" ? 0 : listPrice);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email, and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shop/parts/order", {
        product_id: product.id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim() || null,
        delivery_option: form.delivery_option,
        install_later: !!form.install_later,
        notes: form.notes.trim() || null,
      });
      setConfirmed(data);
      toast.success("Order received — check your email for confirmation.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Order failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="parts-order-modal" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">
            {confirmed ? "Order received" : "Order this part through us"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--ji-muted)]">
            <strong>{product.name}</strong>
            {product.part_number && <> · Part <code className="font-mono text-xs">{product.part_number}</code></>}
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <div className="space-y-4" data-testid="parts-order-confirmed">
            <div className="ji-card p-4 bg-emerald-50 border-emerald-200" style={{ borderRadius: 4 }}>
              <div className="inline-flex items-center gap-2 text-emerald-900 font-semibold text-sm">
                <CheckCircle size={18} weight="fill" /> We&rsquo;ll be in touch within 1 business day
              </div>
              <div className="mt-2 text-xs text-emerald-900/80">
                A team member will call or email you to confirm the order and next steps.
                {form.install_later && " Because you added installation, you also qualify for our free reschedule policy."}
              </div>
            </div>
            <button onClick={() => onOpenChange(false)} className="ji-btn-accent w-full h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>Done</button>
          </div>
        ) : (
          <div className="space-y-4">
            {localCount > 0 && (
              <div className="ji-card p-3 bg-[var(--ji-surface-alt)]" style={{ borderRadius: 4 }} data-testid="parts-local-sources">
                <div className="ji-overline mb-2 inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> Also available locally</div>
                <ul className="space-y-1 text-xs">
                  {product.local_sources.map((ls, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[var(--ji-accent)]">•</span>
                      <span>
                        <strong>{ls.name}</strong>{ls.price ? ` — $${Number(ls.price).toFixed(2)}` : ""}
                        {ls.address && <span className="text-[var(--ji-muted)]"> · {ls.address}</span>}
                        {ls.phone && <> · <a href={`tel:${ls.phone}`} className="text-[var(--ji-primary)]">{ls.phone}</a></>}
                        {ls.notes && <span className="text-[var(--ji-muted)]"> · {ls.notes}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-sm">Full name *</Label><Input data-testid="parts-name" value={form.name} onChange={set("name")} className="h-11 mt-1" placeholder="Jane Doe" /></div>
              <div><Label className="text-sm">Phone *</Label><Input data-testid="parts-phone" value={form.phone} onChange={set("phone")} className="h-11 mt-1" placeholder="(504) 555-0100" /></div>
              <div className="md:col-span-2"><Label className="text-sm">Email *</Label><Input type="email" data-testid="parts-email" value={form.email} onChange={set("email")} className="h-11 mt-1" placeholder="you@email.com" /></div>
              <div className="md:col-span-2"><Label className="text-sm">Address (delivery / install)</Label><Input data-testid="parts-address" value={form.address} onChange={set("address")} className="h-11 mt-1" placeholder="Metairie, LA" /></div>
              <div className="md:col-span-2">
                <Label className="text-sm">Appliance model / notes</Label>
                <Textarea data-testid="parts-notes" rows={2} value={form.notes} onChange={set("notes")} className="mt-1" placeholder="e.g. Fridge model WRS325FDAM04 · symptoms · timing" />
              </div>
            </div>

            <div>
              <Label className="ji-overline">How should we get it to you?</Label>
              <div className="mt-2 grid grid-cols-1 gap-2" data-testid="parts-delivery-options">
                {[
                  { id: "ship", label: "Ship to my door", price: `$${listPrice.toFixed(2)}`, desc: `Standard delivery. Includes ${promo?.shop_parts_markup_pct ?? 25}% service markup.` },
                  { id: "pickup", label: "We pick up locally & drop off", price: `$${listPrice.toFixed(2)} + $${pickupFee.toFixed(2)} pickup fee`, desc: `We grab it from a local supplier and bring it to you — faster than shipping.` },
                  { id: "self", label: "I&rsquo;ll grab it myself locally", price: "Free", desc: `Buy from a local store above and we&rsquo;ll install it later — no reschedule fee.` },
                ].map((o) => (
                  <button
                    type="button"
                    key={o.id}
                    data-testid={`parts-delivery-${o.id}`}
                    onClick={() => set("delivery_option")(o.id)}
                    className={`text-left p-3 border-2 transition-colors ${
                      form.delivery_option === o.id
                        ? "border-[var(--ji-accent)] bg-[var(--ji-surface-alt)]"
                        : "border-[var(--ji-border)] hover:border-[var(--ji-muted)]"
                    }`}
                    style={{ borderRadius: 4 }}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div dangerouslySetInnerHTML={{ __html: `<strong>${o.label}</strong>` }} className="text-sm" />
                      <span className="text-sm font-bold text-[var(--ji-primary)] whitespace-nowrap">{o.price}</span>
                    </div>
                    <div className="text-xs text-[var(--ji-muted)] mt-1" dangerouslySetInnerHTML={{ __html: o.desc }} />
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-2 p-3 bg-[var(--ji-surface-alt)] cursor-pointer text-sm" style={{ borderRadius: 4 }}>
              <input type="checkbox" checked={!!form.install_later} onChange={set("install_later")} data-testid="parts-install-later" className="w-4 h-4 accent-[var(--ji-accent)] mt-0.5" />
              <span>
                <strong>Also install this for me.</strong>{" "}
                <span className="text-[var(--ji-muted)]">Free rescheduling if the part arrives late — we&rsquo;ll come back on your schedule at no extra charge.</span>
              </span>
            </label>

            <div className="flex items-baseline justify-between border-t border-[var(--ji-border)] pt-3">
              <span className="text-sm text-[var(--ji-muted)]">Est. part total (labor billed separately)</span>
              <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-primary)]" data-testid="parts-est-total">${optionTotal.toFixed(2)}</span>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => onOpenChange(false)} className="ji-btn-outline px-4 h-10 text-sm font-semibold" style={{ borderRadius: 4 }} disabled={submitting}>Cancel</button>
              <button onClick={submit} data-testid="parts-order-submit" disabled={submitting} className="ji-btn-accent inline-flex items-center gap-2 px-5 h-10 text-sm font-semibold disabled:opacity-60" style={{ borderRadius: 4 }}>
                {submitting ? <Spinner size={14} className="animate-spin" /> : <ShoppingCart size={14} weight="bold" />}
                {submitting ? "Sending…" : "Send order request"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function ShopPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [q, setQ] = useState("");
  const [zip, setZip] = useState(() => {
    try { return localStorage.getItem("ji_zip") || ""; } catch { return ""; }
  });
  const [loading, setLoading] = useState(true);
  const [promo, setPromo] = useState(null);
  const [orderProduct, setOrderProduct] = useState(null);
  const [lightboxProduct, setLightboxProduct] = useState(null);

  const loadProducts = React.useCallback(async (zipCode) => {
    try {
      const url = zipCode ? `${API}/shop/products?zip=${encodeURIComponent(zipCode)}` : `${API}/shop/products`;
      const res = await fetch(url);
      setProducts(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [catRes, promoRes] = await Promise.all([
          fetch(`${API}/shop/categories`),
          fetch(`${API}/settings/promo`),
        ]);
        setCategories(await catRes.json());
        setPromo(await promoRes.json());
        await loadProducts(zip);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.message) || "Failed to load shop");
      } finally { setLoading(false); }
    })();
  }, []);

  // Auto-open lightbox when landing on /shop?product=<id> (deep link from Home
  // "Recently viewed" strip or share links).
  useEffect(() => {
    if (!products || products.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("product");
    if (!wanted) return;
    const match = products.find((p) => p.id === wanted);
    if (match) {
      trackProductView(match);
      setLightboxProduct(match);
    }
  }, [products]);

  // Refetch when ZIP changes (with a small debounce)
  useEffect(() => {
    const t = setTimeout(() => {
      if (zip && zip.length >= 5) {
        try { localStorage.setItem("ji_zip", zip); } catch { /* ignore */ }
        loadProducts(zip);
      } else if (!zip) {
        try { localStorage.removeItem("ji_zip"); } catch { /* ignore */ }
      }
    }, 400);
    return () => clearTimeout(t);
  }, [zip, loadProducts]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return products.filter((p) => {
      if (activeCategory !== "all" && p.category !== activeCategory) return false;
      if (!ql) return true;
      const haystack = [
        p.name, p.brand, p.description, p.supplier,
        p.part_number, p.model_number,
        ...(Array.isArray(p.compatible_models) ? p.compatible_models : []),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(ql);
    });
  }, [products, q, activeCategory]);

  const trackClick = (product) => {
    // fire-and-forget click tracking — do NOT prevent default navigation
    try {
      const url = `${API}/shop/products/${product.id}/click`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url);
      } else {
        api.post(`/shop/products/${product.id}/click`).catch(() => {});
      }
    } catch { /* silent */ }
  };

  const countBy = (catId) => products.filter((p) => catId === "all" || p.category === catId).length;

  return (
    <div data-testid="shop-page">
      {/* Hero */}
      <section className="ji-container pt-16 md:pt-24 pb-8">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Storefront size={12} weight="fill" /> The Joseph Industries Shop
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-4xl">
          Appliances, parts, cleaning &amp; furniture —<br />
          <span className="text-[var(--ji-accent)] italic">delivered to your door.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          A hand-picked catalog of the same brands and parts our technicians trust — shipped directly to you by our supplier partners.
        </p>
        <p className="mt-3 max-w-2xl text-sm text-[var(--ji-muted)] inline-flex items-start gap-2">
          <ShieldCheck size={14} weight="bold" className="text-[var(--ji-accent)] mt-1 flex-shrink-0" />
          <span>
            Clicking &ldquo;Buy&rdquo; takes you to our supplier&rsquo;s secure checkout. Joseph Industries may earn a small commission at no cost to you.
          </span>
        </p>
        <div className="mt-5 inline-flex items-center gap-3 px-4 py-3 border-2 border-[var(--ji-accent)] bg-[var(--ji-accent)]/5" style={{ borderRadius: 4 }} data-testid="shop-discount-callout">
          <Tag size={18} weight="duotone" className="text-[var(--ji-accent)] flex-shrink-0" />
          <div className="text-sm">
            <strong className="text-[var(--ji-primary)]">Bonus:</strong> Every purchase from this shop qualifies for
            <strong className="text-[var(--ji-accent)]"> 10% off installation &amp; repair</strong> on that item. Just mention it when you book.
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-3 px-4 py-3 bg-[var(--ji-primary)]/5 border border-[var(--ji-primary)]/20" style={{ borderRadius: 4 }} data-testid="shop-parts-callout">
          <Truck size={18} weight="duotone" className="text-[var(--ji-primary)] flex-shrink-0" />
          <div className="text-sm">
            <strong className="text-[var(--ji-primary)]">Need a part?</strong> Search by <em>model number</em> or <em>part number</em> below.
            Enter your <strong>ZIP</strong> to see local stores near you — or order through us for &ldquo;ship to your door&rdquo; plus <strong>free install rescheduling</strong>.
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="ji-container pb-6" data-testid="shop-filter-bar">
        <div className="ji-card p-4 md:p-5 space-y-4" style={{ borderRadius: 4 }}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
              <Input
                data-testid="shop-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by model # · part # · brand · keyword…"
                className="h-11 pl-9"
              />
            </div>
            <div className="relative">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
              <Input
                data-testid="shop-zip"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                placeholder="ZIP code (optional)"
                className="h-11 pl-9"
                inputMode="numeric"
                maxLength={5}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="shop-category-tabs">
              <button
                data-testid="shop-cat-all"
                onClick={() => setActiveCategory("all")}
                className={`inline-flex items-center gap-2 h-11 px-4 text-sm font-semibold transition-colors ${
                  activeCategory === "all"
                    ? "bg-[var(--ji-primary)] text-white"
                    : "bg-[var(--ji-surface-alt)] text-[var(--ji-text)] hover:bg-[var(--ji-border)]"
                }`}
                style={{ borderRadius: 4 }}
              >
                <Sparkle size={14} weight="bold" /> All <span className="opacity-70">({countBy("all")})</span>
              </button>
              {categories.map((c) => {
                const Icon = CATEGORY_ICON[c.id] || Tag;
                const active = activeCategory === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`shop-cat-${c.id}`}
                    onClick={() => setActiveCategory(c.id)}
                    className={`inline-flex items-center gap-2 h-11 px-4 text-sm font-semibold transition-colors ${
                      active
                        ? "bg-[var(--ji-primary)] text-white"
                        : "bg-[var(--ji-surface-alt)] text-[var(--ji-text)] hover:bg-[var(--ji-border)]"
                    }`}
                    style={{ borderRadius: 4 }}
                  >
                    <Icon size={14} weight="bold" /> {c.label} <span className="opacity-70">({countBy(c.id)})</span>
                  </button>
                );
              })}
            </div>
        </div>
      </section>

      {/* Grid */}
      <section className="ji-container pb-24" data-testid="shop-grid-section">
        {loading ? (
          <div className="py-24 text-center text-[var(--ji-muted)]">
            <Spinner size={22} className="animate-spin mx-auto" /> Loading shop…
          </div>
        ) : filtered.length === 0 ? (
          <div className="ji-card p-14 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>
            <ShoppingCart size={32} weight="duotone" className="mx-auto mb-3 text-[var(--ji-accent)]" />
            <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl font-bold text-[var(--ji-text)]">No products found.</div>
            <p className="mt-2 text-sm">Try a different category or search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="shop-grid">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`shop-product-${p.id}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4), duration: 0.35, ease: "easeOut" }}
                className="ji-card flex flex-col overflow-hidden group hover:border-[var(--ji-accent)] transition-colors"
                style={{ borderRadius: 4 }}
              >
                <div className="relative aspect-[4/3] bg-[var(--ji-surface-alt)] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { trackProductView(p); setLightboxProduct(p); }}
                    data-testid={`shop-image-btn-${p.id}`}
                    aria-label={`View larger photo of ${p.name}`}
                    className="block w-full h-full cursor-zoom-in"
                  >
                    <img
                      src={p.image_url}
                      alt={p.name}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=800&q=70"; }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </button>
                  {p.badge && (
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[var(--ji-accent)] text-white font-bold" style={{ borderRadius: 4 }}>
                      {p.badge}
                    </span>
                  )}
                  {(p.local_sources && p.local_sources.length > 0) && (
                    <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-white/95 text-[var(--ji-primary)] font-bold inline-flex items-center gap-1" style={{ borderRadius: 4 }}>
                      <MapPin size={10} weight="fill" /> Local
                    </span>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  {p.brand && (
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)]">{p.brand}</div>
                  )}
                  <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-1 text-base font-bold tracking-tight text-[var(--ji-text)] line-clamp-2">
                    {p.name}
                  </h3>
                  {p.part_number && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-[var(--ji-muted)] font-mono">
                      <Barcode size={11} weight="bold" /> Part #{p.part_number}
                    </div>
                  )}
                  {p.compatible_models && p.compatible_models.length > 0 && (
                    <div className="mt-1 text-[10px] text-[var(--ji-muted)] line-clamp-1" title={p.compatible_models.join(", ")}>
                      Fits: {p.compatible_models.slice(0, 3).join(", ")}{p.compatible_models.length > 3 ? ` +${p.compatible_models.length - 3} more` : ""}
                    </div>
                  )}
                  {p.description && (
                    <p className="mt-2 text-xs text-[var(--ji-muted)] leading-relaxed line-clamp-2">{p.description}</p>
                  )}
                  <div className="mt-4 flex items-baseline justify-between border-t border-[var(--ji-border)] pt-3">
                    <div>
                      <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-primary)]">
                        ${Number(p.list_price ?? p.price).toFixed(2)}
                      </div>
                      {p.list_price && Number(p.list_price) !== Number(p.price) && (
                        <div className="text-[10px] text-[var(--ji-muted)]">Via us · incl. service markup</div>
                      )}
                    </div>
                    {p.supplier && (
                      <span className="text-[10px] text-[var(--ji-muted)]">Ships from {p.supplier}</span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-col gap-2">
                    <button
                      onClick={() => setOrderProduct(p)}
                      data-testid={`shop-order-${p.id}`}
                      className="ji-btn-accent inline-flex items-center justify-center gap-2 px-4 h-11 text-sm font-semibold w-full"
                      style={{ borderRadius: 4 }}
                    >
                      <Truck size={14} weight="bold" /> Order through us
                    </button>
                    <a
                      href={p.affiliate_url}
                      target="_blank"
                      rel="noopener noreferrer sponsored nofollow"
                      onClick={() => trackClick(p)}
                      data-testid={`shop-buy-${p.id}`}
                      className="ji-btn-outline inline-flex items-center justify-center gap-2 px-4 h-10 text-xs font-semibold w-full"
                      style={{ borderRadius: 4 }}
                    >
                      Or buy direct on {p.supplier || "Amazon"} <ArrowUpRight size={12} weight="bold" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <PartsOrderModal
        open={!!orderProduct}
        onOpenChange={(v) => { if (!v) setOrderProduct(null); }}
        product={orderProduct}
        promo={promo}
      />

      <ProductLightbox
        product={lightboxProduct}
        onClose={() => setLightboxProduct(null)}
        onOrder={(p) => setOrderProduct(p)}
        onBuyDirect={(p) => trackClick(p)}
      />
    </div>
  );
}
